import { useState, useMemo, useCallback, useEffect } from "react";
import type { BiddingInfoItem, BiddingInfoSubItem } from "../../../types";
import type { ItemStatusType } from "../../../constants";
import {
  BiddingItemStatusConst,
  CaltivationCategoryConst,
  CombinedCategoryOptions,
  ListingCategoryConst,
  parseCombinedCategory,
  PrefTypeConst,
  ProductionHistoryConst,
} from "../../../constants";
import { useAuth } from "../../../contexts/AuthContext";
import { useItemFilters } from "../../../contexts/ItemFilterContext";
import { useMasters } from "../../../contexts/MastersContext";
import {
  useBiddingItemsGetItemListQueryWithControl,
  useCreateBiddingItemMutation,
  useDeleteBiddingItemMutation,
  useUpdateBiddingItemMutation,
  useUpdateBiddingItemStatusMutation,
  useUpsertSubItemsMutation,
} from "../../../hooks/useBiddingItemsQuery";
import { BiddingItemMaintenanceFilterDialog } from "../../../components/admin/BiddingItemMaintenance/BiddingItemMaintenanceFilterDialog";
import { BiddingItemMaintenanceItemRow } from "../../../components/admin/BiddingItemMaintenance/BiddingItemMaintenanceItemRow";
import { BiddingItemMaintenanceEditForm } from "../../../components/admin/BiddingItemMaintenance/BiddingItemMaintenanceEditForm";
import { useGetBiddingDateForApplyQuery } from "../../../hooks/useCalendarQuery";
import { UserRole } from "../../../types/UserRole";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { useBiddingInfoPdfUrlQuery } from "../../../hooks/useBiddingReportQuery";
import { TEA_CATEGORY } from "../../../constants/TeaCategoryConst";
import { RefreshCw } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";

dayjs.extend(utc);
dayjs.extend(timezone);

// デフォルトのタイムゾーンを日本時間(JST)に設定
dayjs.tz.setDefault("Asia/Tokyo");

//--------------------------------
// 出品管理のメインコンポーネント
//--------------------------------
export const BiddingItemMaintenancePage = () => {
  //----------------
  // Context使用定義
  //----------------

  // ★★Context
  // 目的:
  // コンポーネントをまたがってグローバルに扱いたいstateや関数を管理する
  // 目的：
  // １．Props Drilling（プロップス・ドリリング）の解消
  // 　　=>親から子、子から孫、孫から曾孫へと、何層ものコンポーネントを経由してPropsをバケツリレーする手間（これをProps Drillingと呼びます）がなくなります。
  // 　　 コードがすっきりし、保守性が大幅に向上します。
  // ２．アプリケーション全体の状態管理の一元化
  // 　　ユーザー認証情報、UIテーマ（ダークモード/ライトモード）、言語設定、グローバルな通知状態など、アプリケーションのどこからでもアクセスしたい情報を一元管理します。
  // 　　=>どのコンポーネントも同じ「情報源（Source of Truth）」を参照するため、データの整合性が保たれます。
  // ３．関心の分離とテスト容易性（※カスタムフックと連携した場合）
  // 　　状態管理ロジックをContext（とカスタムフック）に閉じ込める。
  // 　　=>コンポーネント本体はContextの存在を知らなくても、提供されるカスタムフック（例: useAuth()）を呼び出すだけでデータを利用できます。
  // 　　 これにより、コンポーネントのテストが容易になります（テストの際はダミーのProviderでラップするだけ）。
  // 使うべき場面:
  // 　　アプリケーション全体に関わる共通データ、または非常に深い階層間で共有したいデータがある場合。
  // 注意点:
  // 　　乱用は避けるべきです。Contextは、それが更新されると、そのContextを使用しているすべての子孫コンポーネントを再レンダリングさせる可能性があります。
  // 　　パフォーマンスの問題を防ぐため、コンポーネントのローカルなステート (useState) で十分な場合は、そちらを優先すべきです。

  const queryClient = useQueryClient();
  const { currentUser } = useAuth(); // 認証context
  const { itemFilters, updateFiltersAndNavigate } = useItemFilters(); // 検索条件context
  const {
    isMastersLoading,
    sellers,
    farmers,
    gaps,
    teaTypes,
    teaSeasons,
    varieties,
  } = useMasters(); // マスタcontex

  const { data: initialBiddingDate, isLoading: isDateLoading } = useGetBiddingDateForApplyQuery(currentUser?.loginId);
  
  //コンソール確認用
  console.log("APIから届いた日付:", initialBiddingDate);
  console.log("現在のフィルター状態:", itemFilters.biddingDate);
  //----------------
  // State定義
  //----------------

  // ★★useState
  // 再レンダリングのトリガー: stateとして定義したオブジェクトの内容に変更が発生すると、コンポーネントが再レンダリングされる。
  // useState は、現在の値を参照する変数（ゲッター）と、値を更新するためのセッター関数の組み合わせで定義する。
  // セッター経由で値の変更を行うことで、レンダリングを誘発させることができる。
  // 定義時に初期値の設定も任意に行える。
  // ※ 注意 ※ セッターによる更新は非同期で行われる。
  // 　 　　 　 =>セッター経由で値の更新を行った直後にゲッター経由で値を参照しても、変更の内容が反映されているとは限らない
  // ※ セッターには、新しい値そのものを渡す方法と、**直前の値を受け取って新しい値を返す関数を渡す方法（関数形式アップデート）**の2種類がある。
  // 　 例１：新しい値そのものを渡す
  // 　 　　　setCount(5)
  // 　 例２：現在の値を受け取る関数を渡す（関数形式アップデート）
  // 　 　　　setCount(prevCount => prevCount + 1)
  // 　 　　　直前のステート値に基づいて次の値を計算する場合は、「関数形式アップデート」を使うのが推奨される。

  const [splittingItemId, setSplittingItemId] = useState<string | null>(null); // 分割編集中明細の出品ID
  const [editingItemId, setEditingItemId] = useState<string | null>(null); // 編集中明細の出品ID
  const [isCreatingNewItem, setIsCreatingNewItem] = useState(false); // 新規登録モードの状態管理(true:新規登録モード中)
  const [isFilterOpen, setIsFilterOpen] = useState(false); // 検索ダイアログ表示の状態管理(true:検索ダイアログ表示中)
  const [isInitialized, setIsInitialized] = useState(false);　 // 1. 初期化完了フラグ
  const [createInitialData, setCreateInitialData] = useState<BiddingInfoItem | null>(null);
  const [isPrinting, setIsPrinting] = useState(false);
  //----------------
  // Memo定義
  //----------------

  // ★★useMemo
  // レンダリングの都度再作成させたくない値などを保管(キャッシュ)させるための仕組み
  // =>memo定義していないと、親コンポーネントのレンダリングの都度、オブジェクトは再作成される
  //   オブジェクトの内容が変わらなくても、オブジェクト自体が再作成されるため、そのオブジェクトをPropsで渡されている子コンポーネントも再作成されてしまう
  // =>memo定義しておくと、内容が変わらない限りはオブジェクトの再作成がされない為、そのオブジェクトをPropsで渡されている子コンポーネントの再レンダリングがスキップされ、パフォーマンスが向上する
  // useMemoを定義する際には、該当オブジェクト内でstateや親コンポーネントから渡されたPropsの値を使用している場合は、それらを依存配列に定義すべき
  // =>オブジェクト内で使用しているstateなどの値を反映させたかたちでオブジェクトの再作成を行う必要があるから
  // ==>再作成しないと古い状態のstateの値のままのオブジェクトとなってしまう
  // ※ 依存配列を指定ないと＝＞一番最初にレンダリングした際に定義し、それ以降の再レンダリングでは一切再作成されない
  // 　 =>完全な定数などは依存配列を指定しないで定義する

  // マスタcontextから取得した各マスタデータ
  const masterDataForComponents = useMemo(
    () => ({
      varieties,
      teaTypes,
      teaSeasons,
      sellers,
      farmers,
      gaps,
    }),
    [varieties, teaTypes, teaSeasons, sellers, farmers, gaps],
  );

  const isSeller = currentUser?.role === UserRole.Seller;
  const isJA = currentUser?.role === UserRole.JA;
  const isFarmer = currentUser?.role === UserRole.Farmer;
  
  // 入札日の検索条件変更
  const handleDateChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedDate = e.target.value; 

    updateFiltersAndNavigate({
      ...itemFilters,
      biddingDate: selectedDate ? dayjs(selectedDate).format("YYYYMMDD") : ""
    });
  }, [itemFilters, updateFiltersAndNavigate]);

  //県内県外判定
  //セレクトボックス
  const handleCombinedCategoryChange = useCallback((selectedValue: string) => {
  if (selectedValue === "") {
    // 「すべて」が選ばれた場合、区分も地区も検索条件から外す
    updateFiltersAndNavigate({
      ...itemFilters,
      listingCategory: undefined, 
      prefectureType: undefined, 
    });
    return;
  }

  const { listingCategory, prefCategory } = parseCombinedCategory(selectedValue);
  updateFiltersAndNavigate({
    ...itemFilters,
    listingCategory: listingCategory,
    prefectureType: prefCategory, 
  });
  }, [itemFilters, updateFiltersAndNavigate]);

  const blankItem: BiddingInfoItem = useMemo(() => {
    let initialSellerCode: number | "" = ""; //Undefindではなく空文字をかえすように
    let initialFarmerCode: number | "" = "";

    // 売手、または生産者の場合：売手コード（親）をセット
    if (isSeller || isFarmer) {
      initialSellerCode = currentUser?.parentCode ? Number(currentUser.parentCode) : "";
    }

    // 生産者の場合のみ：生産者コード（子）をセット
    if (isFarmer) {
      initialFarmerCode = currentUser?.childCode ? Number(currentUser.childCode) : "";
    }

    return {
      itemId: "",
      receptionNumber: "",
      receptionDate: "",
      varietyCode: "",
      varietyName: "",
      teaTypeCode: "",
      teaTypeName: "",
      teaSeasonCode: "",
      teaSeasonName: "",
      sellerCode: initialSellerCode, // 権限に応じた初期値
      sellerName: "",
      farmerCode: initialFarmerCode, // 権限に応じた初期値
      farmerName: "",
      brand: "",
      capacity1: "",
      quantity1: "",
      capacity2: "",
      quantity2: "",
      gapCode: "",
      gapName: "",
      caltivationCategory: CaltivationCategoryConst.CONVENTIONAL,
      productionHistory: ProductionHistoryConst.YES,
      biko: "",
      status: BiddingItemStatusConst.INPUT,
      listingCategory: ListingCategoryConst.BIDDING,
      biddingDate: "",
      subItems: [],
      bidMinPrice: 0,
      teaCategory: TEA_CATEGORY.ARACHA,
      isFarmerHidden: false,
      biddingNumber: "",
    };
    // 依存配列に権限関連の変数
  }, [isSeller, isFarmer, currentUser]);

    //----------------
    // Effect定義 (初期化処理)
    //----------------
    useEffect(() => {
      // 1. データの読み込み待機
      if (isDateLoading || isMastersLoading || !currentUser) return;
    
      // すでに初期化が完了している場合は、無駄な判定をスキップ
      if (isInitialized) return;
    
      // --- A. ログインユーザーからデフォルト地区（1:県内 or 2:県外）を特定  ---
      let defaultPref: number = PrefTypeConst.INTERNAL; 
      const targetSellerCode = currentUser?.parentCode;
      if (targetSellerCode) {
        const myData = sellers.find(s => String(s.sellerCode) === String(targetSellerCode));
        if (myData) {
          defaultPref = myData.prefectureType;
        }
      }
    
      // --- B. URLフィルターの初期化  ---
      const newFilters = { ...itemFilters };
      let hasChanged = false;
    
      // 入札日の初期化
      if (!newFilters.biddingDate) {
        newFilters.biddingDate = initialBiddingDate ? String(initialBiddingDate) : dayjs().tz().format("YYYYMMDD");
        hasChanged = true;
      }
      // 出品区分の初期化
      if (!newFilters.listingCategory) {
        newFilters.listingCategory = ListingCategoryConst.BIDDING;
        hasChanged = true;
      }
      // 地区の初期化
      if (!newFilters.prefectureType) {
        newFilters.prefectureType = defaultPref;
        hasChanged = true;
      }
    
      // 売手・生産者の初期検索条件セット
      // 売手または生産者の場合、売手コードをセット
      if ((isSeller || isFarmer) && !newFilters.sellerCode) {
        newFilters.sellerCode = currentUser?.parentCode ? Number(currentUser.parentCode) : "";
        hasChanged = true;
      }
    
      // 生産者の場合のみ、生産者コードをセット
      if (isFarmer && !newFilters.farmerCode) {
        newFilters.farmerCode = currentUser?.childCode ? Number(currentUser.childCode) : "";
        hasChanged = true;
      }
    
      // --- C. 初期化の確定判定 ---
      if (hasChanged) {
        // 値がセットされていない項目があった場合、URL(Context)を更新する
        // この時点では isInitialized は false のままなので、APIはまだ飛ばない
        updateFiltersAndNavigate(newFilters);
      } else {
        // すべての値がセット済みの状態（hasChangedがfalse）なら、
        // ここで初めてAPI実行を許可するフラグを立てる
        setIsInitialized(true);
      }
    
    }, [
      initialBiddingDate, 
      isDateLoading, 
      isMastersLoading, 
      isSeller, 
      isFarmer, 
      sellers, 
      currentUser, 
      itemFilters, 
      isInitialized, // フラグを依存配列に追加
      updateFiltersAndNavigate
    ]);

    

  //----------------
  // Hooks使用定義
  //----------------

  // ★★フック(Hooks)
  // 特定のロジックをコンポーネントの外に切り出してまとめたもの
  // 目的:
  // １．ロジックの再利用可
  // ２．関心の分離
  // 　　コンポーネント内のコードを、UIロジック（表示やイベントハンドラ）と非UIロジック（データ取得、認証、複雑な計算、外部API連携など）に明確に分離する
  // 　　=>コンポーネントは**「UIのレンダリング」**という本来の責務に集中できる
  // 　　=>コンポーネントファイルが読みやすくなり、どこに何が書かれているかが明確になる
  // ３．テスト容易性の向上
  // 　　コンポーネント本体のテストをシンプルにする
  // 　　=>ロジック（計算が正しいか、APIが正しく呼ばれるかなど）はフック単体でテスト可能になる

  const createItemMutation = useCreateBiddingItemMutation(); // 出品アイテム登録
  const updateItemMutation = useUpdateBiddingItemMutation(); // 出品アイテム変更
  const deleteItemMutation = useDeleteBiddingItemMutation(); // 出品アイテム削除
  const updateStatusMutation = useUpdateBiddingItemStatusMutation(); // 出品ステータス更新
  const upsertSubItemsMutation = useUpsertSubItemsMutation(); // 出品サブアイテム登録
  // const {data: items, isLoading, error,} = useBiddingItemsGetItemListQuery(itemFilters, currentUser?.loginId ?? ""); // 出品アイテム一覧取得
  // const {data: items, isLoading, error,} = useBiddingItemsGetItemListQuery(getItemsFiltersToday(itemFilters), currentUser?.loginId ?? ""); // 出品アイテム一覧取得
  // const { data: items, isLoading, error } = useBiddingItemsGetItemListQuery(itemFilters, currentUser?.loginId ?? "");

  // 1. データ取得の条件(enabled)を定義
  // 入札日がセットされている、またはURLにクエリがある場合のみAPIを叩く
  const shouldFetch = isInitialized && !!itemFilters.biddingDate;

  const {
    data: responseData,
    isLoading,
    isFetching,
    error,
  } = useBiddingItemsGetItemListQueryWithControl(
    itemFilters,
    currentUser?.loginId ?? "",
    { enabled: shouldFetch }, 
  );

  // 一括更新用のハンドラ
  const handleRefreshAll = async () => {
    // 1. 一覧データのキャッシュを無効化
    queryClient.invalidateQueries({ queryKey: ['biddingitems'] }); 
    
    // 2. ヘッダーの件数（CommonLayoutで使用しているキー）のキャッシュを無効化
    queryClient.invalidateQueries({ queryKey: ['biddingitemsstatusupdate'] });
  };

  // PDF取得用フックの呼び出し（enabled: falseにして手動実行に）
  const { refetch: fetchPdf, isFetching: isPdfLoading } = useBiddingInfoPdfUrlQuery(itemFilters, {
    enabled: false,
  });
  //----------------
  // イベントハンドラー定義
  //----------------

  // ★★useCallback
  // useCallbackで囲む際には、該当関数内でstateや親コンポーネントから渡されたPropsの値を使用している場合は、それらを依存配列に定義すべき
  // =>関数内で使用しているstateなどの値を反映させたかたちで関数の再作成を行う必要があるから
  // ==>再作成しないと古い状態のstateの値のままで動く関数となってしまう

  // 新規登録フォーム展開
  const handleToggleCreateNew = useCallback(() => {
    setCreateInitialData({
      ...blankItem,
      // 現在 Datepicker（itemFilters）で選択されている日付を最優先にする
      biddingDate: itemFilters.biddingDate 
        ? String(itemFilters.biddingDate) 
        : (initialBiddingDate ? String(initialBiddingDate) : ""),
    });

    setIsCreatingNewItem(true);
    setEditingItemId(null);
    setSplittingItemId(null);
  }, [blankItem, initialBiddingDate, itemFilters.biddingDate]);

  // 分割編集フォーム展開
  const handleToggleSplit = useCallback((itemId: string) => {
    // 新規登録モード解除
    setIsCreatingNewItem(false);
    // 編集中明細の出品IDクリア
    setEditingItemId(null);
    // 分割編集中明細の出品ID指定
    setSplittingItemId((p) => (p === itemId ? null : itemId));
  }, []);

  // 編集フォーム展開
  const handleToggleEdit = useCallback((itemId: string) => {
    // 新規登録モード解除
    setIsCreatingNewItem(false);
    // 編集中明細の出品ID指定
    setEditingItemId((p) => (p === itemId ? null : itemId));
    // 分割編集中明細の出品IDクリア
    setSplittingItemId(null);
  }, []);

  // コピー機能のハンドラーを追加
  const handleCopyItem = useCallback((item: BiddingInfoItem) => {
  const copiedItem: BiddingInfoItem = {
    ...item,            // 全ての項目を一旦コピー
    itemId: "",         // IDは新規発行するので空にする
    receptionNumber: "", // 受付番号も新しいものになるので空にする
    brand: "",            // 荷印を空にする
    status: BiddingItemStatusConst.INPUT, // ステータスは未提出に戻す
    subItems: [],       // 分割データは空にする
    capacity1:"",
    quantity1:"",
    capacity2:"",
    quantity2:"",
    bidMinPrice:"",
    biko:"",
  };

  setCreateInitialData(copiedItem); // 加工したデータをセット
  setIsCreatingNewItem(true);       // 新規登録モードON
  setEditingItemId(null);
  setSplittingItemId(null);
}, []);

  // 出品アイテム新規登録実行
  const handleSaveNewItem = useCallback((newItem: BiddingInfoItem) => {
    // ★★mutate
    createItemMutation.mutate(
      // 第１引数
      { newItemData: newItem },
      // オプション引数
      {
        onSuccess(data, variables, context) {
          // 成功時の固有処理
          console.info("新規登録成功(data):", data);
          console.info("新規登録成功(variables):", variables);
          console.info("新規登録成功(context):", context);
          // 新規登録モード解除
          setIsCreatingNewItem(false);
        },

        onError(error, variables, context) {
          // エラー時の固有処理
          console.error("新規登録エラー(error):", error);
          console.error("新規登録エラー(variables):", variables);
          console.error("新規登録エラー(context):", context);
          // toast.error(error.message);
        },

        onSettled(data, error, variables, context) {
          // 成功・失敗での共通処理
          console.info("新規登録(data):", data);
          console.info("新規登録(variables):", variables);
          console.info("新規登録(context):", context);
          if (error) {
            console.error("新規登録(error):", error);
          }
        },
      },
    );
  }, []);

  // 出品アイテム更新実行
  const handleSaveEdit = useCallback((updatedItem: BiddingInfoItem) => {
    updateItemMutation.mutate(
      { updateItemData: updatedItem },
      {
        onSuccess: () => {
          // 分割編集中明細の出品IDクリア
          setEditingItemId(null);
        },
      },
    );
  }, []);

  // 出品アイテム削除実行
  const handleDeleteItem = useCallback((itemId: string) => {
    if (confirm("この出品情報を本当に削除しますか？")) {
      deleteItemMutation.mutate({ itemId: itemId });
    }
  }, []);

  // 出品サブアイテム登録実行
  const handleUpsertSubItems = useCallback(
    (itemId: string, newSubItems: BiddingInfoSubItem[]) => {
      if (newSubItems.length === 0) {
        handleDeleteSubItems(itemId);
        return;
      }
      upsertSubItemsMutation.mutate(
        { itemId: itemId, requests: newSubItems },
        {
          onSuccess: () => {
            // 分割編集中明細の出品IDクリア
            setSplittingItemId(null);
          },
        },
      );
    },
    [],
  );

  // 出品サブアイテム削除実行
  const handleDeleteSubItems = useCallback((itemId: string) => {
    const emptyRequests: BiddingInfoSubItem[] = [];
    upsertSubItemsMutation.mutate(
      { itemId, requests: emptyRequests },
      {
        onSuccess: () => {
          // 分割編集中明細の出品IDクリア
          setSplittingItemId(null);
        },
      },
    );
  }, []);

  // 出品アイテムステータス更新実行
  const handleStatusChange = useCallback(
    (itemId: string, newStatus: ItemStatusType) => {
      updateStatusMutation.mutate({ itemId: itemId, status: newStatus });
    },
    [],
  );

  // 直接印刷ハンドラの実装
    const handlePrintDirect = useCallback(async () => {
      if (isPdfLoading || isPrinting) return;
      
      try {
        setIsPrinting(true);
        
        const { data: pdfUrl } = await fetchPdf();
        if (!pdfUrl) {
          setIsPrinting(false);
          return;
        }
    
        // 既存の印刷用iframeがあれば削除
        const oldIframe = document.getElementById('pdf-print-iframe');
        if (oldIframe) document.body.removeChild(oldIframe);
    
        const iframe = document.createElement('iframe');
        iframe.id = 'pdf-print-iframe';
        iframe.style.position = 'fixed';
        iframe.style.right = '0';
        iframe.style.bottom = '0';
        iframe.style.width = '0';
        iframe.style.height = '0';
        iframe.style.border = 'none';
        iframe.src = pdfUrl;
        document.body.appendChild(iframe);
    
        iframe.onload = () => {
          // PDFの読み込み完了後、少し待ってから印刷ダイアログを起動
          setTimeout(() => {
            iframe.contentWindow?.focus();
            iframe.contentWindow?.print();
            
            // ダイアログが立ち上がったタイミングで「準備中...」を解除
            setIsPrinting(false);
            URL.revokeObjectURL(pdfUrl);
          }, 500);
        };
      } catch (err) {
        console.error(err);
        setIsPrinting(false);
      }
    }, [fetchPdf, isPdfLoading, isPrinting]);

  //----------------
  // その他定数定義
  //----------------
  const isPageLoading = isLoading || isMastersLoading || isDateLoading || !isInitialized;

  //----------------
  // レンダリング処理
  //----------------
  if (isPageLoading) {
    return (
      <div className="bg-white min-h-screen font-sans flex items-center justify-center">
        <p className="text-lg text-gray-600">読み込み中...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white min-h-screen font-sans flex items-center justify-center p-4">
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative" role="alert">
          <strong className="font-bold">エラーが発生しました:</strong>
          <span className="block sm:inline"> {error.message}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white min-h-screen font-sans relative">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 lg:py-12">
        <header className="mb-8">
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-6">
            <div className="flex items-center gap-4">
              <h1 className="text-3xl font-extrabold text-slate-800">出品情報の編集</h1>
            </div>
            <div className="flex items-center gap-3">
              <button onClick={() => setIsFilterOpen(true)} 
                    className="px-4 py-2 text-sm font-medium text-indigo-700 bg-indigo-50  hover:bg-indigo-100 active:bg-indigo-200 active:scale-95 transition-all rounded-lg duration-150 border border-indigo-200"
              >詳細検索</button>
              <button onClick={handleToggleCreateNew} 
                    className="px-4 py-2 text-sm font-medium text-white      bg-indigo-600 hover:bg-indigo-700 active:bg-indigo-200 active:scale-95 transition-all rounded-lg shadow-sm "
                >
                  新規登録</button>
            </div>
          </div>

          {/* フィルターエリア全体 */}
          <div className="bg-slate-50 p-4 rounded-lg border border-slate-200 grid grid-cols-2 gap-4 items-end sm:flex sm:items-end sm:gap-6">
            
            {/* 入札日 */}
            <div className="col-span-1 w-full sm:w-auto">
              <label className="block text-sm font-bold text-slate-700 mb-1">入札日</label>
              <input 
                type="date" 
                value={itemFilters.biddingDate ? dayjs(String(itemFilters.biddingDate).replace(/^(\d{4})(\d{2})(\d{2})$/, "$1-$2-$3")).format("YYYY-MM-DD") : ""} 
                onChange={handleDateChange}
                className="w-full sm:w-48 p-2 h-[40px] border border-slate-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500 bg-white appearance-none leading-tight safari-date-fix"
              />
            </div>

            {/* 出品区分 */}
            <div className="col-span-1 w-full sm:w-auto">
              <label className="block text-sm font-bold text-slate-700 mb-1">出品区分（県内/県外）</label>
              <select
                disabled={isSeller || isFarmer || isJA}
                value={(itemFilters.listingCategory && itemFilters.prefectureType) ? `${itemFilters.listingCategory}_${itemFilters.prefectureType}` : ""} 
                onChange={(e) => handleCombinedCategoryChange(e.target.value)}
                className={`w-full sm:w-56 p-2 h-[40px] border border-slate-300 rounded-md ${
                  (isSeller || isFarmer || isJA) ? "bg-slate-100 text-slate-500" : "bg-white"
                }`}
              >
                {CombinedCategoryOptions.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>

            {/* 印刷ボタン：Farmer以外 */}
            {!isFarmer && (
              <div className="col-span-2 sm:col-span-1 sm:ml-auto">
                <button
                  type="button"
                  disabled={isPdfLoading || isPrinting}
                  onClick={handlePrintDirect}
                  className="w-30 h-[40px] flex items-center justify-center bg-indigo-600 hover:bg-indigo-700 disabled:bg-indigo-400 text-white 
                  rounded-md shadow-md transition-all duration-200 font-bold active:scale-95 whitespace-nowrap"
                >
                  {isPdfLoading ? '準備中...' : '印刷プレビュー'}
                </button>
              </div>
            )}
          </div>
        </header>

        <main className="sm:overflow-x-auto pb-4">
          {responseData && !responseData.isSuccess ? (
            <div className="text-center py-10 text-gray-500">
              <span>{responseData.message}</span>
            </div>
          ) : (
          <div className="w-full sm:min-w-[1200px]">
            {responseData?.items && responseData.items.length > 0 && (
             <div className="sticky top-0 z-10 bg-white hidden sm:flex items-end gap-4 px-4 pb-2 pt-4 text-xs font-bold text-slate-500 border-b-2 border-slate-200 min-w-full">
                <div className="flex-1 flex flex-col gap-2">
                  <div className="flex items-center gap-8">
                    <div className="w-24">受付No.</div>
                    <div className="flex items-center gap-3">
                      <div className="w-[88px] ml-2">受付日</div>
                      <div className="w-[88px] ml-2">入札日</div>
                    </div>
                    <div className="flex items-center gap-6 flex-1">
                      <div className="w-1/2 ml-1">売手</div>
                      <div className="w-1/2">生産者</div>
                    </div>
                  </div>
                  <div className="grid grid-cols-[3rem_0.5rem_3rem_0.5rem_3.5rem_0.5rem_3.5rem_0.5rem_4rem_0.5rem_4rem_0.5rem_1fr] items-center text-center">
                    <span className="-translate-x-1">入札No.</span>
                    <span></span>
                    <span>茶区分</span>
                    <span></span>
                    <span>茶期</span>
                    <span></span>
                    <span>茶種</span>
                    <span></span>
                    <span>品種名</span>
                    <span></span>
                    <span>GAP区分</span>
                    <span></span>
                    <div className="ml-2 text-left">荷印</div>
                  </div>
                  <div className="flex items-center justify-between mt-0.5">
                    <div className="flex-1 grid grid-cols-[3.5rem_0.5rem_4.5rem_0.5rem_3.5rem_0.5rem_1fr] items-center text-center">
                      <span>出品区分</span>
                      <span></span>
                      <span>栽培区分</span>
                      <span></span>
                      <span>生産履歴</span>
                      <span></span>
                      <div className="text-left ml-2">備考</div>
                    </div>
                    <div className="flex items-center gap-6 w-[360px] justify-end pr-4">
                      <div className="w-[140px] text-center">容量(kg) × 本数(本)</div>
                      <div className="text-right w-20">総量(kg)</div>
                      <div className="text-right w-28">最低落札値(円)</div>
                    </div>
                  </div>
                </div>
                <div className="w-32 flex-shrink-0"></div>
              </div>
            )}
            {responseData?.items?.map((item) => (
              <BiddingItemMaintenanceItemRow
                key={item.itemId}
                item={item}  
                isSplitting={splittingItemId === item.itemId}
                isEditing={editingItemId === item.itemId}
                onToggleSplit={() => handleToggleSplit(item.itemId)}
                onToggleEdit={() => handleToggleEdit(item.itemId)}
                onSaveEdit={handleSaveEdit}
                onConfirmSplit={(newSubItems) => handleUpsertSubItems(item.itemId, newSubItems)}
                onStatusChange={(newStatus) => handleStatusChange(item.itemId, newStatus)}
                onDelete={() => handleDeleteItem(item.itemId)}
                onCopy={handleCopyItem}
                isSaving={(updateItemMutation.isPending && updateItemMutation.variables?.updateItemData.itemId === item.itemId) || (updateStatusMutation.isPending && updateStatusMutation.variables?.itemId === item.itemId) || (upsertSubItemsMutation.isPending && upsertSubItemsMutation.variables?.itemId === item.itemId) || (deleteItemMutation.isPending && deleteItemMutation.variables?.itemId === item.itemId)}
                masterData={masterDataForComponents}
                isSeller={isSeller}
                isFarmer={isFarmer}
              />
            ))}
            {!isPageLoading && (!responseData?.items || responseData.items.length === 0) && !isCreatingNewItem && (
                <div className="text-center py-10 text-gray-500">表示する出品情報がありません。</div>
            )}
          </div>
          )}
        </main>

        {isCreatingNewItem && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-fadeIn">
            <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
              <div className="overflow-y-auto">
                <BiddingItemMaintenanceEditForm
                  originalItem={createInitialData || blankItem}
                  onSave={handleSaveNewItem}
                  onCancel={() => { setIsCreatingNewItem(false); setCreateInitialData(null); }}
                  isCreating={true}
                  isSaving={createItemMutation.isPending}
                  masterData={masterDataForComponents}
                  isSeller={isSeller}
                  isFarmer={isFarmer}
                />
              </div>
            </div>
          </div>
        )}
        <BiddingItemMaintenanceFilterDialog isOpen={isFilterOpen} onClose={() => setIsFilterOpen(false)} masterData={masterDataForComponents} isSeller={isSeller} isFarmer={isFarmer} />
      </div>
      <button
        onClick={handleRefreshAll}
        disabled={isFetching || isPageLoading}
        className="fixed bottom-6 left-6 sm:bottom-10 sm:left-10 z-30 p-3 sm:p-3.5 bg-indigo-600 text-white rounded-full shadow-lg hover:bg-indigo-700 hover:shadow-xl transition-all active:scale-95 disabled:bg-indigo-400 focus:outline-none focus:ring-4 focus:ring-indigo-300"
        title="最新の情報に更新"
      >
        <RefreshCw className={`w-5 h-5 sm:w-6 sm:h-6 ${isFetching ? 'animate-spin' : ''}`} />
      </button>
    </div>
  );
};